import { Inject, inject, Injectable, LOCALE_ID } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'shared/environments/environment';
import { AudioTrack } from 'shared/src/components/audio-track/audio-track.component';
import { CustomizerSettingsService } from 'shared/src/services/customizer-settings.service';
import { LocalStorageKey, LocalStorageService } from 'shared/src/services/local-storage.service';
import {
  FunnelSampleContentService,
  pickNarratorAvatar,
} from 'shared/src/services/funnel-sample-content.service';
import {
  NewsbreakAgeRange,
  NewsbreakGender,
  NewsbreakService,
} from 'shared/src/services/newsbreak.service';
import { StripeService } from 'shared/src/services/stripe.service';
import type { BookDto } from 'shared/src/types/book-dto';
import { Book } from 'shared/src/types/funnel-sample-content';
import { AnalyticsService } from '../../../../shared/src/services/analytics.service';
import {
  ExperimentCell,
  ExperimentName,
  StatsIGService,
} from 'shared/src/services/stats-ig.service';
// All ID types now imported from quiz.ts
import { UserInfoService } from 'shared/src/services/user-info.service';
import type { RiveAnimationConfig } from 'shared/src/types/rive-animation-config';
import { FlowScreen, FunnelFlowConfig, QuizStepKey } from '../flow/flow-config';
import {
  findNextShownScreen,
  isBackAllowed,
  isConditionMet,
  resolveFlow,
  screenRoute,
  validateFlow,
} from '../flow/flow-engine';
import { funnelFlow } from '../funnel.flow';
import {
  anxietyBookIdsMap,
  challengesBookIdMap,
  defaultBookIds,
  findPromotedBookId,
  goalsBookIdsMap,
  interleaveBookIds,
  mainFunnelGoalBookIdsMap,
  mainFunnelMotivationBookIdsMap,
  motivationBookIdMap,
  personTypeBookIdsMap,
  procrastinationBookIdsMap,
  BookId,
  PromotedBookId,
} from '../data/books';
import { COMPETITORS } from '../data/competitors';
import { DEMO_PAIRS, DemoPairKey } from '../data/demos';
import {
  AgeRangeId,
  anxietyLevels,
  AnxietyLevelId,
  audioExperiences,
  AudioExperienceId,
  celebrities,
  Celebrity,
  ChallengeId,
  contentFormats,
  ContentFormatId,
  defaultMotivationIds,
  fictionInterests,
  FictionInterestId,
  GenderId,
  GoalId,
  goalMotivationsMap,
  goals,
  goalTitlePhrasing,
  highlightText,
  MotivationId,
  motivations,
  nonFictionInterests,
  NonFictionInterestId,
  personTypes,
  PersonTypeId,
  procrastinationFrequencies,
  ProcrastinationFrequencyId,
  Quiz,
  quizSteps,
  ReaderGoalId,
} from '../data/quiz';
import { getAnswerIds } from '../data/result-helpers';
import { goalsAudioIdsMap } from '../data/sounds';
import { ResultPlanMessageService, ResultPlanReadingList } from './result-plan-message.service';
import { PROJECT_NAME, ProjectName, ProjectNames } from 'shared/src/tokens';

declare const $localize: any;

enum ConsumptionTime {
  morning,
  commute,
  workingOut,
  break,
}

// 'other' and 'preferNotToTell' are answers we cannot segment on, so Amplitude gets a
// single 'unknown' bucket instead of two near-empty ones.
const UNKNOWN_DEMOGRAPHIC = 'unknown';

function genderUserProperty(gender: GenderId): string {
  return gender === 'other' ? UNKNOWN_DEMOGRAPHIC : gender;
}

function ageRangeUserProperty(age: AgeRangeId): string {
  return age === 'preferNotToTell' ? UNKNOWN_DEMOGRAPHIC : age;
}

const NEWSBREAK_GENDERS: Record<GenderId, NewsbreakGender> = {
  female: 0,
  male: 1,
  other: 2,
};

// Our answers straddle NewsBreak's fixed buckets, so each maps to the one holding most of its
// span: 21-40 lands in 25-34, 41-54 in 45-54. 'age55plus' is open-ended and covers both of their
// top buckets (55-64 and 65+) with no way to tell which, so it — like 'preferNotToTell' — maps to
// nothing and clears any age NewsBreak already holds.
const NEWSBREAK_AGE_RANGES: Partial<Record<AgeRangeId, NewsbreakAgeRange>> = {
  under21: 1,
  age2140: 2,
  age4154: 4,
};

export type { QuizStepKey };

type WebQuizAnswers = { [key: string]: unknown };

// The answers the result screens read from memory, which a reload would otherwise leave empty:
// the step whose answer sets the field, the key its answer is stored under, and its options.
// kindleAudioExperience comes after audioExperience so it wins when both are stored.
const RESTORED_ANSWERS: readonly [QuizStepKey, string, Record<string, unknown>][] = [
  ['audioExperience', 'audioExperience', audioExperiences],
  ['kindleAudioExperience', 'kindleAudioExperience', audioExperiences],
  ['anxiety', 'anxiety', anxietyLevels],
  ['procrastination', 'procrastination', procrastinationFrequencies],
  ['fictionInterests', 'fictionInterests', fictionInterests],
  ['nonFictionInterests', 'nonFictionInterests', nonFictionInterests],
  // The book and demo picks are ranked from these, and the plan result matches its reading list
  // against the picks, so they must come out the same after a reload.
  ['personType', 'personType', personTypes],
  ['format', 'formats', contentFormats],
];

type AbCells = { [N in ExperimentName]?: ExperimentCell<N> };

const DEFAULT_DEMO_PAIR_ORDER: DemoPairKey[] = [
  'ebooks',
  'education',
  'web',
  'email',
  'selfhelp',
  'truecrime',
  'romance',
  'religious',
];

const FORMAT_DEMO_PAIRS: Record<ContentFormatId, DemoPairKey> = {
  webArticles: 'web',
  emails: 'email',
  ebooks: 'ebooks',
  ownWriting: 'selfhelp',
  documentsPdfs: 'education',
  booksPrintedTexts: 'romance',
  religiousTexts: 'religious',
  aiChatsResearch: 'web',
  youtubeVideos: 'web',
  other: 'truecrime',
};

const GOAL_DEMO_PAIRS: Partial<Record<GoalId, DemoPairKey[]>> = {
  learnSomethingNew: ['education', 'web', 'ebooks'],
  beMoreProductive: ['email', 'web', 'selfhelp'],
  listenOnTheGo: ['truecrime', 'web', 'ebooks'],
  makeReadingEasier: ['selfhelp', 'education', 'web'],
  adhdEyeStrainDyslexia: ['selfhelp', 'education', 'web'],
  everythingIsPerfect: ['ebooks', 'web', 'email'],
};

export type QuizSteps = {
  [K in QuizStepKey]: Quiz;
};

@Injectable({
  providedIn: 'root',
})
export class FunnelFlowService {
  // Headline from the ad's `?title=`; undefined when absent or blank.
  private customIntroTitle: string | undefined;

  // False for funnels that ignore `?title=` entirely: their first screen stays as designed and
  // web_onboarding_started carries no intro-title properties.
  protected readonly isCustomIntroTitleSupported: boolean = true;

  // Book promised by the ad creative (via the `book` query param); it must
  // be presented among the book-sample steps and reported to the server.
  private promotedBookId: PromotedBookId | undefined;

  protected steps: QuizSteps = quizSteps;

  private times: ConsumptionTime[] = [];

  protected gender: GenderId | undefined;
  private age: AgeRangeId | undefined;
  private goalId: GoalId | undefined;
  private motivationIds: MotivationId[] = [];
  private readerGoalIds: ReaderGoalId[] = [];
  private nonFictionInterestIds: NonFictionInterestId[] = [];
  private fictionInterestIds: FictionInterestId[] = [];
  private lastPlayedTrack: AudioTrack | undefined;

  private challengeIds: ChallengeId[] = [];

  // Captured competitor from ads
  private competitor: string | undefined;

  private webQuizAnswers: WebQuizAnswers = {};

  private book1: Book;
  private book2: Book;
  private book3: Book;

  // Narrator avatar chosen per book (keyed by title) for the current
  // book1/book2/book3/yourBook slots, so the four sample screens never
  // show the same portrait twice unless the avatar pool runs out.
  private narratorAvatarMap: Map<string, string> = new Map();

  private audioLearningExperience: AudioExperienceId | undefined;
  private anxietyFeeling: AnxietyLevelId | undefined;
  private procrastinationFrequency: ProcrastinationFrequencyId | undefined;
  private personalityType: PersonTypeId | undefined;
  private selectedFormatIds: ContentFormatId[] = [];

  // Injected rather than a constructor parameter so the AppLovin factory, which hand-threads the
  // constructor's dependencies, and the subclasses' super() calls stay untouched.
  private readonly abService = inject(StatsIGService);

  // Read at bootstrap for every test in the flow config; see getAbCell().
  private readonly abCells: AbCells = {};

  private readonly resultPlanMessageService = inject(ResultPlanMessageService);

  constructor(
    protected router: Router,
    protected localStorageService: LocalStorageService,
    protected userInfo: UserInfoService,
    protected analyticsService: AnalyticsService,
    protected stripeService: StripeService,
    protected sampleContentService: FunnelSampleContentService,
    @Inject(LOCALE_ID) private localeId: string,
    @Inject(PROJECT_NAME) protected projectName: ProjectName,
    private themeService: CustomizerSettingsService,
    private newsbreakService: NewsbreakService
  ) {
    const storedGoalId = this.localStorageService.get(LocalStorageKey.goal) as GoalId | undefined;

    if (storedGoalId && goals[storedGoalId]) {
      this.goalId = storedGoalId;
    }

    this.gender = this.localStorageService.get(LocalStorageKey.gender) as GenderId | undefined;
    this.age = this.localStorageService.get(LocalStorageKey.age) as AgeRangeId | undefined;

    // Resend on a resumed session: the stored answers outlive the Amplitude device id.
    const storedDemographics = {
      ...(this.gender ? { web_gender: genderUserProperty(this.gender) } : {}),
      ...(this.age ? { web_age_range: ageRangeUserProperty(this.age) } : {}),
    };
    if (Object.keys(storedDemographics).length) {
      this.analyticsService.identify(storedDemographics);
    }

    // Same for NewsBreak, which also backfills visitors who were already past these steps when
    // the mapping shipped and will never answer them again.
    const storedNewsbreakDemographics = {
      ...(this.gender ? { gender: NEWSBREAK_GENDERS[this.gender] } : {}),
      ...(this.age ? { ageRange: NEWSBREAK_AGE_RANGES[this.age] ?? null } : {}),
    };
    if (Object.keys(storedNewsbreakDemographics).length) {
      this.newsbreakService.identify(storedNewsbreakDemographics);
    }

    // Initialize motivation IDs from localStorage
    const storedMotivationIds = this.localStorageService.get<MotivationId[]>(
      LocalStorageKey.motivations
    );
    if (storedMotivationIds && Array.isArray(storedMotivationIds)) {
      this.motivationIds = storedMotivationIds;
    }

    const storedReaderGoalIds = this.localStorageService.get<ReaderGoalId[]>(
      LocalStorageKey.readerGoals
    );
    if (storedReaderGoalIds && Array.isArray(storedReaderGoalIds)) {
      this.readerGoalIds = storedReaderGoalIds;
    }

    const storedWebQuizAnswers = this.localStorageService.get<WebQuizAnswers>(
      LocalStorageKey.webQuizAnswers
    );
    if (
      storedWebQuizAnswers &&
      typeof storedWebQuizAnswers === 'object' &&
      !Array.isArray(storedWebQuizAnswers)
    ) {
      this.webQuizAnswers = storedWebQuizAnswers;
    }
    this.restoreAnswersForResult();

    // Initialize competitor from localStorage
    const storedCompetitor = this.localStorageService.get<string>(LocalStorageKey.competitor);
    if (storedCompetitor) {
      this.competitor = storedCompetitor;
    }

    const flowConfig = this.getFlowConfig();

    if (!environment.production) {
      const errors = validateFlow(flowConfig, Object.keys(quizSteps));

      if (errors.length) {
        throw new Error(`Invalid funnel flow config:\n${errors.join('\n')}`);
      }
    }

    for (const { experiment, exposedOn, exposedWhen } of flowConfig.experiments ?? []) {
      (this.abCells as Record<ExperimentName, string>)[experiment] = this.abService.getCell(
        experiment,
        { logExposure: !exposedOn && !exposedWhen }
      );
    }
  }

  /**
   * The funnel's screens in order (see flow-config.ts). A method rather than a field because the
   * constructor reads it, before a subclass's field initializers have run.
   */
  protected getFlowConfig(): FunnelFlowConfig {
    return funnelFlow;
  }

  setCompetitor(keyword: string | null | undefined): void {
    if (!keyword) {
      return;
    }

    const raw = keyword
      .toLowerCase()
      .replace(/["'\[\]\(\)]/g, ' ')
      .trim();

    let detected: string | undefined;
    for (const item of COMPETITORS) {
      if (item.matchers.some(m => raw.includes(m))) {
        detected = item.name;
        break;
      }
    }

    if (detected) {
      this.competitor = detected;
      this.localStorageService.set(LocalStorageKey.competitor, detected);
    }
  }

  getCompetitor(): string | undefined {
    return this.competitor;
  }

  goToStart() {
    this.router.navigate(['/quiz']);
  }

  setTitle(title: string | undefined) {
    if (!this.isCustomIntroTitleSupported) {
      return;
    }

    this.customIntroTitle = title || undefined;
  }

  setPromotedBook(param: string | null) {
    // Fall back to the stored value so the promise survives page refreshes;
    // stored values are re-validated in case the promoted list changed.
    this.promotedBookId =
      findPromotedBookId(param) ??
      findPromotedBookId(this.localStorageService.get<string>(LocalStorageKey.promotedBook));

    if (this.promotedBookId) {
      this.localStorageService.set(LocalStorageKey.promotedBook, this.promotedBookId);
    }
  }

  // The flow is resolved on every read rather than once: it depends on the promoted book, which
  // the app sets only after this service is built.
  getScreen(index: number): FlowScreen | undefined {
    return this.getScreens()[index];
  }

  isBackAllowed(index: number): boolean {
    return isBackAllowed(this.getScreens(), index);
  }

  // Progress counts quiz steps only, so the bar moves the same whatever screens sit between them.
  getProgress(index: number): number {
    const quizIndexes = this.getQuizIndexes();

    return (100 * (quizIndexes.indexOf(index) + 1)) / quizIndexes.length;
  }

  // Carries the terms footer, also when an intro screen comes before it.
  isFirstQuizStep(index: number): boolean {
    return this.getQuizIndexes()[0] === index;
  }

  // For the screens that are not quiz steps and have nothing to save: intro, customization, ready.
  handleFinishedScreen(index: number) {
    if (index === 0) {
      this.startNewQuizRun();
    }

    this.goToNextScreen(index);
  }

  handleFinishedLogin(stripeCustomerId: string, email: string) {
    this.userInfo.setEmail(email);
    this.userInfo.setStripeCustomerId(stripeCustomerId);

    this.goToNextScreen(this.getScreens().findIndex(({ type }) => type === 'login'));
  }

  handleFinishedManualFormat(index: number, format: string) {
    const formatToSave = format.trim();

    if (!formatToSave) {
      return;
    }

    this.setWebQuizAnswer('formatOther', formatToSave);

    this.analyticsService.track('web_format_entered', { format: formatToSave });

    this.goToNextScreen(index);
  }

  handleFinishedStep(index: number, options: string[], data: Quiz) {
    const key = this.getQuizStepKey(index);

    if (!key) {
      return;
    }

    if (index === 0) {
      this.startNewQuizRun();
    }

    this.trackResponse(key, options, data);
    this.saveParameters(key, options);
    this.saveResponse(key, options);
    this.exposeAbTestsAnsweredOn(key);

    this.goToNextScreen(index);
  }

  private goToNextScreen(index: number) {
    const screens = this.getScreens();
    const nextIndex = findNextShownScreen(screens, index, this.webQuizAnswers);

    if (!nextIndex) {
      this.router.navigate(['paywall']);
    } else {
      if (screens[nextIndex].type === 'login' || screens[nextIndex].type === 'paywall') {
        this.stripeService.preloadStripe(); //user is already pretty close to paywall
      }

      this.router.navigate(screenRoute(screens, nextIndex));
    }

    if (index === 0) {
      this.trackFirstScreenPassed();
    }
  }

  // web_first_screen_passed is just for ad networks to map to ViewContent.
  private trackFirstScreenPassed(): void {
    this.analyticsService.track('web_first_screen_passed');
    //this.newsbreakService.viewContent();
  }

  private getScreens(): FlowScreen[] {
    return resolveFlow(this.getFlowConfig(), {
      hasPromotedBook: !!this.getPromotedBook(),
      cellOf: experiment => this.getAbCell(experiment),
    });
  }

  private getQuizIndexes(): number[] {
    return this.getScreens().flatMap((screen, index) => (screen.type === 'quiz' ? [index] : []));
  }

  private getQuizStepKey(index: number): QuizStepKey | undefined {
    const screen = this.getScreen(index);

    return screen?.type === 'quiz' ? screen.step : undefined;
  }

  getLanguage(): string {
    return this.normalizeLocaleToLanguage(this.localeId);
  }

  //used to pass to service to create customer
  getWebQuizAnswers(): WebQuizAnswers {
    return this.webQuizAnswers;
  }

  //used to pass to service to create customer
  getSelectedBooks(): BookDto[] {
    const selectedBooks = [
      this.localStorageService.get(LocalStorageKey.book1),
      this.localStorageService.get(LocalStorageKey.book2),
      this.localStorageService.get(LocalStorageKey.book3),
    ].filter(book => !!book) as BookDto[];

    // The ad-promoted book is reported even if the user didn't like its
    // sample: the ad promised it, so the app should have it ready.
    const promotedBook = this.getPromotedBook();

    if (promotedBook && !selectedBooks.some(book => book.title === promotedBook.title)) {
      selectedBooks.push({ title: promotedBook.title, id: promotedBook.id });
    }

    return selectedBooks;
  }

  getGender(): GenderId | undefined {
    return this.gender;
  }

  getGoalId(): GoalId | undefined {
    return this.goalId;
  }

  // Removed: Use goals[this.goalId] directly when display string is needed

  getSelectedFormats(): ContentFormatId[] {
    return Array.isArray(this.selectedFormatIds) ? this.selectedFormatIds : [];
  }

  getMotivationIds(): MotivationId[] {
    return this.motivationIds;
  }

  getReaderGoalIds(): ReaderGoalId[] {
    return this.readerGoalIds;
  }

  getNonFictionInterestIds(): NonFictionInterestId[] {
    return this.nonFictionInterestIds;
  }

  getFictionInterestIds(): FictionInterestId[] {
    return this.fictionInterestIds;
  }

  getChallengeIds(): ChallengeId[] {
    return this.challengeIds;
  }

  getQuizData(index: number): Quiz | undefined {
    const key = this.getQuizStepKey(index);

    if (key) {
      this.exposeAbTestOn(key);
    }

    return this.getStepData(index);
  }

  /**
   * The properties of web_onboarding_started: the first screen's headline as the visitor sees it,
   * so conversion can be broken down by the ad's custom title versus the funnel's own default
   * (none for funnels that ignore `?title=`), and every A/B test cell the visitor is in as
   * `web_ab_<test>`, so any funnel step can be grouped by cell from the start event.
   */
  getFunnelStartedAnalytics(): Record<string, string | boolean> {
    const properties: Record<string, string | boolean> = {};

    if (this.isCustomIntroTitleSupported) {
      properties['web_intro_title'] = this.stripHTMLTags(this.getStepData(0)?.title ?? '');
      properties['web_intro_title_custom'] = !!this.customIntroTitle;
    }

    for (const { experiment, property } of this.getFlowConfig().experiments ?? []) {
      const cell = this.getAbCell(experiment);

      if (cell) {
        properties[property] = cell;
      }
    }

    return properties;
  }

  rememberLastPlayedTrack(audio: AudioTrack): void {
    this.lastPlayedTrack = audio; // not used atm
  }

  getUserAudioTracks(): { job: AudioTrack[]; areas: AudioTrack[] } {
    const goalId = this.goalId ?? 'beMoreProductive';
    const goalsSounds = this.sampleContentService.getAudioTracks(goalsAudioIdsMap[goalId]);

    // Use challenge IDs directly
    const jobBookIds: BookId[] =
      this.challengeIds.length > 0
        ? this.challengeIds.map(
            id => challengesBookIdMap[id] ?? challengesBookIdMap.everythingIsPerfect
          )
        : [...defaultBookIds];

    const jobBooks = this.sampleContentService.getBooks(jobBookIds);

    return { job: jobBooks, areas: goalsSounds };
  }

  protected getIntroTitle(): string {
    return this.customIntroTitle ?? this.getDefaultIntroTitle();
  }

  protected getDefaultIntroTitle(): string {
    return $localize`:@@introTitleDefault:Boost Your Productivity by Listening`;
  }

  // The cell of an A/B test this funnel's config lists, undefined for any other test.
  protected getAbCell<N extends ExperimentName>(experiment: N): ExperimentCell<N> | undefined {
    return this.abCells[experiment];
  }

  // A test whose screen comes after the first is exposed when that screen first renders (a
  // refresh straight onto it exposes there), so it counts only visitors who reach it.
  private exposeAbTestOn(key: QuizStepKey): void {
    for (const { experiment, exposedOn } of this.getFlowConfig().experiments ?? []) {
      if (exposedOn?.includes(key) && this.getAbCell(experiment)) {
        this.abService.logExposure(experiment);
      }
    }
  }

  // A test that only matters after a given answer counts only the visitors who gave it.
  private exposeAbTestsAnsweredOn(key: QuizStepKey): void {
    for (const { experiment, exposedWhen } of this.getFlowConfig().experiments ?? []) {
      if (exposedWhen?.step === key && isConditionMet(exposedWhen, this.webQuizAnswers)) {
        this.abService.logExposure(experiment);
      }
    }
  }

  // Without exposing any test, so web_onboarding_started can read the first screen.
  private getStepData(index: number): Quiz | undefined {
    const key = this.getQuizStepKey(index);

    if (!key) {
      return undefined;
    }

    const data = this.customizeStepData(key) || this.steps[key];

    return index === 0 ? this.withCustomIntroTitle(data) : data;
  }

  // Funnels opening on the age screen already headline it with getIntroTitle(). The others open
  // on a question (gender, format, wishReadAloud), so an ad's `?title=` takes the headline and
  // the question drops to the subtitle, as the Sep 2026 first-screen test did for wishReadAloud.
  // Without the param the first screen is returned untouched.
  private withCustomIntroTitle(data: Quiz): Quiz {
    if (!this.customIntroTitle || data.title === this.customIntroTitle) {
      return data;
    }

    return { ...data, title: this.customIntroTitle, subtitle: data.title };
  }

  protected customizeStepData(key: QuizStepKey): Quiz | undefined {
    if (key.startsWith('demo')) {
      return this.buildDemoStep(key);
    }

    switch (key) {
      case 'age':
        if (this.getQuizStepKey(0) !== 'age') {
          return {
            ...this.steps.age,
            subtitle: $localize`:@@quizAgeWhySubtitle:Your age helps us tune voice style, pacing, and book recommendations`,
          };
        }

        return {
          ...this.steps.age,
          title: this.getIntroTitle(),
        };

      case 'readingToAudio':
        // The intro title keeps the headline slot it has on the age screen, so the question
        // drops to the subtitle.
        return {
          ...this.steps.readingToAudio,
          title: this.getIntroTitle(),
          subtitle: this.steps.readingToAudio.title,
        };

      case 'personTypeInfo':
        return this.getPersonalityTypeInfo();

      case 'amountInfo':
        let count = '800K';
        let title = $localize`:@@amountInfoIndividuals:Individuals`;

        if (this.gender === 'male') {
          title = $localize`:@@amountInfoMaleIndividuals:Male Individuals`;
          count = '900K';
        } else if (this.gender === 'female') {
          title = $localize`:@@amountInfoFemaleIndividuals:Female Individuals`;
          count = $localize`:@@amountInfoOneMillion:1 Million`;
        }

        const { mediaCite } = this.getAmountInfoContent();

        return {
          ...this.steps.amountInfo,
          rive: {
            ...(this.steps.amountInfo.rive as RiveAnimationConfig),
            animations: [this.getMapAnimationName()],
          },
          // Keep translations free of markup; apply highlight markup in code.
          title:
            $localize`:@@amountInfoJoinPrefix:Join ` +
            highlightText($localize`:@@amountInfoOverText:Over ${count}:COUNT: ${title}:TITLE:`) +
            $localize`:@@amountInfoJoinSuffix: Your Age Worldwide`,
          mediaCite,
        };

      case 'timeForReadingInfo':
        return {
          ...this.steps.timeForReadingInfo,
          rive: {
            ...(this.steps.timeForReadingInfo.rive as RiveAnimationConfig),
            animations: [this.getMapAnimationName()],
          },
        };

      case 'result':
        return this.getResultStep();

      case 'goalInfo':
        return {
          ...this.steps.goalInfo,
          subtitle: this.getGoalInfoSubtitle(),
          rive: this.getGraphRive('peech_graph_1'),
        };

      case 'prediction':
        return {
          ...this.steps.prediction,
          rive: this.getGraphRive('peech_graph_2'),
        };

      case 'audioExperienceInfo':
        return {
          ...this.steps.audioExperienceInfo,
          title:
            this.audioLearningExperience === 'noNotReally'
              ? $localize`:@@discoverAudioPowerPrefix:It's time to discover ` +
                highlightText($localize`:@@powerOfAudio:the power of audio!`)
              : $localize`:@@youLovePeechPrefix:You are going to love ` +
                highlightText($localize`:@@peech:Peech!`),
        };

      case 'book1':
        // Assign all books to ensure consistency
        this.assignBooks();

        return {
          ...this.steps.book1,
          assetId: this.sampleContentService.getBookId(this.book1),
          imageConfig: {
            path: this.book1.imagePath,
          },
          audioTracks: [
            {
              title: this.book1.title,
              audioPath: this.book1.audioPath,
              imagePath: this.getNarratorAvatarPath(this.book1),
            },
          ],
        };

      case 'book2':
        // Assign all books to ensure consistency (handles page refresh)
        this.assignBooks();

        return {
          ...this.steps.book2,
          assetId: this.sampleContentService.getBookId(this.book2),
          imageConfig: {
            path: this.book2.imagePath,
          },
          audioTracks: [
            {
              title: this.book2.title,
              audioPath: this.book2.audioPath,
              imagePath: this.getNarratorAvatarPath(this.book2),
            },
          ],
        };

      case 'book3':
        // Assign all books to ensure consistency (handles page refresh)
        this.assignBooks();

        return {
          ...this.steps.book3,
          assetId: this.sampleContentService.getBookId(this.book3),
          imageConfig: {
            path: this.book3.imagePath,
          },
          audioTracks: [
            {
              title: this.book3.title,
              audioPath: this.book3.audioPath,
              imagePath: this.getNarratorAvatarPath(this.book3),
            },
          ],
        };

      case 'yourBook': {
        // Assign all books to ensure consistency
        this.assignBooks();

        const yourBook = this.sampleContentService.getBook('yourNextReading');

        return {
          ...this.steps.yourBook,
          assetId: 'yourNextReading',
          imageConfig: {
            path: yourBook.imagePath,
          },
          audioTracks: [
            {
              title: yourBook.title,
              audioPath: yourBook.audioPath,
              imagePath: this.getNarratorAvatarPath(yourBook),
            },
          ],
        };
      }

      case 'celeb1': {
        const celeb1 = this.getCelebLineup()[0];
        return {
          ...this.steps.celeb1,
          subtitle: celeb1.name,
          imageConfig: {
            path: celeb1.imageFilePath,
            maxWidth: '200px',
          },
        };
      }
      case 'celeb2': {
        const celeb2 = this.getCelebLineup()[1];
        return {
          ...this.steps.celeb2,
          subtitle: celeb2.name,
          imageConfig: {
            path: celeb2.imageFilePath,
            maxWidth: '200px',
          },
        };
      }
      case 'celeb3': {
        const celeb3 = this.getCelebLineup()[2];
        return {
          ...this.steps.celeb3,
          subtitle: celeb3.name,
          imageConfig: {
            path: celeb3.imageFilePath,
            maxWidth: '200px',
          },
        };
      }

      case 'goalMotivations':
        if (!this.goalId) {
          return {
            ...this.steps.goalMotivations,
            title:
              $localize`:@@quizGoalMotivTitlePrefix:What do you hope to ` +
              highlightText($localize`:@@achieve:achieve`) +
              $localize`:@@quizGoalMotivTitleWithPeechSuffix: with Peech?`,
            options: Object.entries(motivations)
              .filter(([id]) => defaultMotivationIds.includes(id as MotivationId))
              .map(([id, title]) => ({ id, title })),
          };
        }

        // Get the appropriate phrasing and relevant motivations for this goal
        const goalPhrase = goalTitlePhrasing[this.goalId];
        const relevantMotivIds = goalMotivationsMap[this.goalId];

        return {
          ...this.steps.goalMotivations,
          title:
            $localize`:@@quizGoalMotivTitlePrefix:What do you hope to ` +
            highlightText($localize`:@@achieve:achieve`) +
            $localize`:@@quizGoalMotivTitleWithGoalPrefix: with ` +
            goalPhrase +
            $localize`:@@quizGoalMotivTitleQuestion:?`,
          options: Object.entries(motivations)
            .filter(([id]) => relevantMotivIds.includes(id as MotivationId))
            .map(([id, title]) => ({ id, title })),
        };

      default:
        return;
    }
  }

  private getAmountInfoContent(): { subtitle: string; mediaCite: string } {
    let subtitle = $localize`:@@amountInfoDefaultSubtitle:Reading is still how we get smarter in 2025 - the best knowledge is in text. Change how you take it in.`;

    const techCrunchQuote = $localize`:@@amountInfoTechCrunchQuote:Peech is a text-to-speech app that actually sounds good`;

    // Default second part of the citation
    let mediaCiteSecondPart = $localize`:@@amountInfoDefaultCite:- a must-have for anyone who wants to learn more but read less.`;

    // Customize subtitle and mediaCite based on user's goal using ID
    if (this.goalId) {
      switch (this.goalId) {
        case 'everythingIsPerfect':
        case 'beMoreProductive':
          subtitle = $localize`:@@amountInfoProdSubtitle:Productivity isn't about working harder - it's about consuming information smarter. Turn reading time into productive listening time.`;
          mediaCiteSecondPart = $localize`:@@amountInfoProdCite:- the productivity boost busy professionals have been waiting for.`;
          break;
        case 'learnSomethingNew':
          subtitle = $localize`:@@amountInfoLearnSubtitle:Learning never stops, but reading time is limited. Transform any text into audio lessons you can absorb anywhere.`;
          mediaCiteSecondPart = $localize`:@@amountInfoLearnCite:- turning every moment into a learning opportunity for curious minds.`;
          break;
        case 'listenOnTheGo':
          subtitle = $localize`:@@amountInfoListenSubtitle:Your commute, workout, or walk doesn't have to be wasted time. Turn articles and books into audio for your daily journey.`;
          mediaCiteSecondPart = $localize`:@@amountInfoListenCite:- turning boring commutes into productive learning time, effortlessly.`;
          break;
        case 'makeReadingEasier':
          subtitle = $localize`:@@amountInfoReadingSubtitle:Reading shouldn't be a struggle. Whether it's eye strain, dyslexia, or just preference - audio makes content accessible.`;
          mediaCiteSecondPart = $localize`:@@amountInfoReadingCite:- making reading accessible for everyone, regardless of their challenges.`;
          break;
        case 'adhdEyeStrainDyslexia':
          subtitle = $localize`:@@amountInfoAdhdSubtitle:Don't let vision problems, ADHD, or other challenges stop your learning. Audio content works with your brain, not against it.`;
          mediaCiteSecondPart = $localize`:@@amountInfoAdhdCite:- transforming how people with health challenges consume and retain information.`;
          break;
      }
    }

    // Combine the TechCrunch quote with our customized second part
    const mediaCite = `${techCrunchQuote} ${mediaCiteSecondPart}`;

    return { subtitle, mediaCite };
  }

  private saveParameters(currentStepKey: QuizStepKey, selectedOptionIds: string[]) {
    this.applyAnswer(currentStepKey, selectedOptionIds);

    switch (currentStepKey) {
      case 'gender': {
        const gender = selectedOptionIds[0] as GenderId;
        this.localStorageService.set(LocalStorageKey.gender, gender);
        this.analyticsService.identify({ web_gender: genderUserProperty(gender) });
        this.newsbreakService.identify({ gender: NEWSBREAK_GENDERS[gender] });
        break;
      }
      case 'age': {
        const ageId = selectedOptionIds[0] as AgeRangeId;
        if (ageId === 'preferNotToTell') {
          // A decline on a revisit has to undo the answer given the first time round.
          this.newsbreakService.identify({ ageRange: null });
        } else {
          this.localStorageService.set(LocalStorageKey.age, ageId);
          this.newsbreakService.identify({ ageRange: NEWSBREAK_AGE_RANGES[ageId] ?? null });
        }
        // Sent even for a decline: an unset property means the user never reached
        // the step, which is a different thing from answering 'unknown'.
        this.analyticsService.identify({ web_age_range: ageRangeUserProperty(ageId) });
        break;
      }
      case 'goals':
        this.localStorageService.set(LocalStorageKey.goal, this.goalId);
        break;
      case 'format':
        this.setWebQuizAnswer('formats', this.selectedFormatIds);
        break;
      case 'challenges':
        this.localStorageService.set(LocalStorageKey.challenges, selectedOptionIds);
        break;
      case 'goalMotivations':
        this.localStorageService.set(LocalStorageKey.motivations, selectedOptionIds);
        break;
      case 'readerGoals':
        this.localStorageService.set(LocalStorageKey.readerGoals, selectedOptionIds);
        break;
    }
  }

  // The in-memory part of an answer only, so restoring one fires none of the side effects above.
  private applyAnswer(currentStepKey: QuizStepKey, selectedOptionIds: string[]) {
    switch (currentStepKey) {
      case 'gender':
        this.gender = selectedOptionIds[0] as GenderId;
        break;
      case 'age':
        if (selectedOptionIds[0] !== 'preferNotToTell') {
          this.age = selectedOptionIds[0] as AgeRangeId;
        }
        break;
      case 'personType':
        this.personalityType = selectedOptionIds[0] as PersonTypeId;
        break;
      case 'goals':
        this.goalId = selectedOptionIds[0] as GoalId;
        break;
      case 'audioExperience':
      case 'kindleAudioExperience':
        this.audioLearningExperience = selectedOptionIds[0] as AudioExperienceId;
        break;
      case 'format':
        this.selectedFormatIds = (selectedOptionIds || []) as ContentFormatId[];
        break;
      case 'anxiety':
        this.anxietyFeeling = selectedOptionIds[0] as AnxietyLevelId;
        break;
      case 'procrastination':
        this.procrastinationFrequency = selectedOptionIds[0] as ProcrastinationFrequencyId;
        break;
      case 'challenges':
        this.challengeIds = selectedOptionIds as ChallengeId[];
        break;
      case 'goalMotivations':
        this.motivationIds = selectedOptionIds as MotivationId[];
        break;
      case 'readerGoals':
        this.readerGoalIds = selectedOptionIds as ReaderGoalId[];
        break;
      case 'nonFictionInterests':
        this.nonFictionInterestIds = selectedOptionIds as NonFictionInterestId[];
        break;
      case 'fictionInterests':
        this.fictionInterestIds = selectedOptionIds as FictionInterestId[];
        break;
    }
  }

  private trackResponse(currentStepKey: QuizStepKey, options: string[], data: Quiz) {
    this.analyticsService.track('web_quiz_step_finished', {
      key: currentStepKey,
      options,
      title: this.stripHTMLTags(data.title), // just to be able to understand what it is in the amplitude dashboard
      // For voting steps without audio tracks (celebs), the subtitle is the
      // celebrity name - track it so vote analysis doesn't have to infer the
      // celebrity from slot × gender.
      asset: data.audioTracks?.[0]?.title ?? (data.isVoting ? data.subtitle : undefined),
      asset_id: data.assetId,
    });
  }

  //used to pass to server later on in case the info is useful enough to customize the UX
  private saveResponse(currentStepKey: QuizStepKey, selectedOptions: string[]) {
    if (
      currentStepKey.includes('Info') ||
      currentStepKey === 'honest' ||
      currentStepKey === 'result'
    ) {
      return;
    }

    switch (currentStepKey) {
      case 'book1':
        if (selectedOptions[0] !== 'no') {
          this.localStorageService.set(LocalStorageKey.book1, {
            title: this.book1.title,
            id: this.book1.id,
          });
        }
        break;
      case 'book2':
        if (selectedOptions[0] !== 'no') {
          this.localStorageService.set(LocalStorageKey.book2, {
            title: this.book2.title,
            id: this.book2.id,
          });
        }
        break;
      case 'book3':
        if (selectedOptions[0] !== 'no') {
          this.localStorageService.set(LocalStorageKey.book3, {
            title: this.book3.title,
            id: this.book3.id,
          });
        }
        break;
      default:
        this.setWebQuizAnswer(currentStepKey, selectedOptions);
    }
  }

  private setWebQuizAnswer(key: string, value: unknown) {
    this.webQuizAnswers[key] = value;
    this.localStorageService.set(LocalStorageKey.webQuizAnswers, this.webQuizAnswers);
  }

  // Answering the first step starts the quiz over: the per-step answers kept from an earlier run
  // are dropped rather than merged into this one, so neither the result screen nor the sign-up
  // payload mixes two runs. The per-field keys (goal, gender, age, motivations) are left as they
  // are; each is overwritten when its own step is answered again. Book votes go too: a "no" never
  // clears its key, so an earlier run's "yes" on the same book would read as liked in this one.
  private startNewQuizRun() {
    this.webQuizAnswers = {};
    this.localStorageService.set(LocalStorageKey.webQuizAnswers, this.webQuizAnswers);
    this.localStorageService.remove(LocalStorageKey.book1);
    this.localStorageService.remove(LocalStorageKey.book2);
    this.localStorageService.remove(LocalStorageKey.book3);
    this.restoreAnswersForResult();
  }

  // Rebuilt from the per-step answers rather than stored separately, so the two never disagree.
  // Cleared first, as a new quiz run restores from no answers at all.
  private restoreAnswersForResult() {
    RESTORED_ANSWERS.forEach(([step]) => this.applyAnswer(step, []));

    for (const [step, answerKey, options] of RESTORED_ANSWERS) {
      const ids = getAnswerIds(this.webQuizAnswers, answerKey, options);

      if (ids.length > 0) {
        this.applyAnswer(step, ids);
      }
    }
  }

  /**
   * Reports the result screen's metric event at most once per visitor per funnel: the result step
   * is rebuilt on every visit, so reloads and back-navigation would otherwise inflate it. Keyed by
   * PROJECT_NAME, so the AppLovin domain counts the main and kindle flows it serves separately.
   */
  protected trackResultMetricOnce(eventName: string, properties: Record<string, unknown>): void {
    const stored = this.localStorageService.get<ProjectName[]>(LocalStorageKey.resultMetricShown);
    const sentFor = Array.isArray(stored) ? stored : [];

    if (sentFor.includes(this.projectName)) {
      return;
    }

    this.analyticsService.track(eventName, properties);
    this.localStorageService.set(LocalStorageKey.resultMetricShown, [...sentFor, this.projectName]);
  }

  protected getResultStep(): Quiz {
    const { graphCaption, cards, metricProperties } = this.resultPlanMessageService.generatePlan({
      answers: this.webQuizAnswers,
      goalId: this.goalId,
      motivationIds: Array.isArray(this.motivationIds) ? this.motivationIds : [],
      procrastinationId: this.procrastinationFrequency,
      anxietyId: this.anxietyFeeling,
      formatIds: this.selectedFormatIds,
      readingList: this.getResultPlanReadingList(),
    });

    this.trackResultMetricOnce('web_personalized_metric_shown', metricProperties);

    const isGrowthGoal = this.goalId === 'beMoreProductive' || this.goalId === 'learnSomethingNew';

    return {
      ...this.steps.result,
      title:
        $localize`:@@kindleResultTitlePrefix:Your ` +
        highlightText($localize`:@@funnelPlanTitleHighlight:listening plan`) +
        $localize`:@@kindleResultTitleSuffix: is ready`,
      messages: undefined,
      rive: this.getGraphRive(isGrowthGoal ? 'peech_graph_2' : 'peech_graph_1'),
      subtitle: graphCaption,
      planCards: cards,
    };
  }

  // The samples the visitor liked in this run, by the rule the sign-up payload uses (anything
  // but a "no"), else books for their goal. Read from stored answers and votes: demo votes exist
  // only in a run without a promoted book, since the first step clears the answers.
  private getResultPlanReadingList(): ResultPlanReadingList {
    const isLiked = (vote: unknown) => Array.isArray(vote) && !!vote[0] && vote[0] !== 'no';
    const demoKeys = (['demo1', 'demo2'] as const).filter(key => key in this.webQuizAnswers);
    let notLikedTitles: string[] = [];

    if (demoKeys.length > 0) {
      // demo1 and demo2 both play the first pair.
      if (demoKeys.some(key => isLiked(this.webQuizAnswers[key]))) {
        return { source: 'liked_demos', demoKinds: [this.getDemoPairs()[0]] };
      }
    } else {
      // Re-ranked from the restored answers, so a vote is only counted for the book this run
      // actually showed in that slot - the stored votes outlive the run that cast them.
      this.assignBooks();

      const shownBooks = [
        { book: this.book1, vote: this.localStorageService.get<BookDto>(LocalStorageKey.book1) },
        { book: this.book2, vote: this.localStorageService.get<BookDto>(LocalStorageKey.book2) },
      ];
      const likedTitles = shownBooks
        .filter(({ book, vote }) => vote?.title === book.title)
        .map(({ book }) => book.title);

      if (likedTitles.length > 0) {
        return { source: 'liked_books', bookTitles: likedTitles };
      }

      notLikedTitles = shownBooks.map(({ book }) => book.title);
    }

    const goalBookIds: readonly BookId[] = this.goalId
      ? mainFunnelGoalBookIdsMap[this.goalId]
      : defaultBookIds;
    const goalBookTitles = Array.from(new Set([...goalBookIds, ...defaultBookIds]))
      .map(id => this.findBook(id)?.title)
      .filter((title): title is string => !!title && !notLikedTitles.includes(title))
      .slice(0, 2);

    return { source: 'goal_books', bookTitles: goalBookTitles };
  }

  private findBook(id: BookId): Book | undefined {
    try {
      return this.sampleContentService.getBook(id);
    } catch {
      return undefined;
    }
  }

  private stripHTMLTags(input: string): string {
    return input.replace(/<[^>]*>/g, '');
  }

  protected getGoalInfoSubtitle(): string {
    let message: string;

    switch (this.goalId) {
      case 'everythingIsPerfect':
      case 'beMoreProductive':
        message = $localize`:@@goalInfoProd:Over 1 million professionals have boosted their productivity with Peech's audio learning approach. You're next.`;
        break;

      case 'learnSomethingNew':
        message = $localize`:@@goalInfoLearn:Join 600,000+ lifelong learners who use Peech to turn educational content into audio they can listen to anytime.`;
        break;

      case 'listenOnTheGo':
        message = $localize`:@@goalInfoListen:More than 900,000 busy people have reclaimed their time with Peech by turning reading into listening on the go.`;
        break;

      case 'makeReadingEasier':
        message = $localize`:@@goalInfoReading:Over 800,000 people have transformed their reading experience with Peech's audio learning features. Join them today.`;
        break;

      case 'adhdEyeStrainDyslexia':
        message = $localize`:@@goalInfoAdhd:Join 550,000 people who use Peech to make reading accessible despite vision or cognitive challenges.`;
        break;

      default:
        message = $localize`:@@goalInfoDefault:Peech has helped over 3 million people continue learning and growing every day. Let us do the same for you.`;
    }

    const note = this.getGoalInfoSubtitleNote();
    return `${message}<br/><i class="fs-12 muted-color">${note}</i>`;
  }

  protected getGoalInfoSubtitleNote(): string {
    return $localize`:@@goalInfoCitation:*Matthews G. Study on Goal-Setting, 2015`;
  }

  // The branded map.riv contains "Male", "Female" and "Both" animations.
  protected getMapAnimationName(): string {
    if (this.gender === 'male') {
      return 'Male';
    }

    if (this.gender === 'female') {
      return 'Female';
    }

    return 'Both';
  }

  // peech_graph_1: daily Peech vs mindless scrolling over a month. peech_graph_2: week-by-week
  // focus and learning milestones. One animation per locale, in a dark and a white file.
  private getGraphRive(graph: 'peech_graph_1' | 'peech_graph_2'): RiveAnimationConfig {
    const step = graph === 'peech_graph_1' ? this.steps.goalInfo : this.steps.prediction;

    return {
      ...(step.rive as RiveAnimationConfig),
      fileName: `${graph}_${this.themeService.isDark() ? 'dark' : 'white'}`,
      animations: [this.getGraphLocalizedAnimationName()],
    };
  }

  private getGraphLocalizedAnimationName(): string {
    const locale = this.normalizeLocaleToLanguage(this.localeId);
    const supportedRiveLocales = ['en', 'pt', 'fr', 'es', 'de', 'tr', 'pl', 'it'];

    return supportedRiveLocales.includes(locale) ? locale : 'en';
  }

  private normalizeLocaleToLanguage(localeId: string): string {
    return (localeId || '').trim().replace('_', '-').toLowerCase().split('-')[0];
  }

  private getPersonalityTypeInfo(): Quiz {
    let riveFileName = 'ambivert';
    let subtitle = $localize`:@@ambivertExplanationAudioExperience:People who identify as extroverts or introverts based on the situation are termed ambiverts. With this in mind, we will tailor a unique approach to your audio experience.`;
    let title = $localize`:@@welcomeAmbivertClub:Welcome to the Ambivert Club!`;

    if (this.personalityType === 'extrovert') {
      riveFileName = 'extrovert';
      title = $localize`:@@welcomeExtrovertClub:Welcome to the Extrovert Club!`;
      subtitle = $localize`:@@extrovertExplanation:Extroverts tend to gain energy from social interactions and prefer learning through discussion and collaboration. We'll customize your audio experience to match your outgoing personality.`;
    } else if (this.personalityType === 'introvert') {
      riveFileName = 'introvert';
      title = $localize`:@@welcomeIntrovertClub:Welcome to the Introvert Club!`;
      subtitle = $localize`:@@introvertExplanation:Introverts often prefer reflective learning and deep concentration. We'll tailor your audio experience to provide a thoughtful, focused approach to content.`;
    }

    return {
      ...this.steps.personTypeInfo,
      title,
      subtitle,
      rive: {
        fileName: riveFileName,
        ratio: 1.5 / 1,
        animations: ['Timeline 1'],
      },
    };
  }

  private assignBooks(): void {
    // An ad-promoted book overrides whatever relevance ranking the funnel
    // computed: it must be the first sample the user hears.
    const promotedBook = this.getPromotedBook();
    const rankedBooks = this.getRelevantBooks().filter(book => book.title !== promotedBook?.title);
    const relevantBooks = promotedBook ? [promotedBook, ...rankedBooks] : rankedBooks;

    const fallbackBooks = this.sampleContentService.getBooks(defaultBookIds);

    this.book1 = relevantBooks[0] || fallbackBooks[0];
    this.book2 = this.findDiverseBook(relevantBooks, [this.book1]) || fallbackBooks[1];
    this.book3 =
      this.findDiverseBook(relevantBooks, [this.book1, this.book2]) ||
      this.findBookExcluding(relevantBooks, [this.book1, this.book2]) ||
      fallbackBooks[2] ||
      fallbackBooks[0];

    this.narratorAvatarMap = this.buildNarratorAvatarMap([
      this.book1,
      this.book2,
      this.book3,
      this.sampleContentService.getBook('yourNextReading'),
    ]);
  }

  // Reserves each slot's explicit books.json override first (only if that
  // portrait isn't already claimed by an earlier slot), then fills the
  // remaining slots from the gender pool so book1/book2/book3/yourBook
  // never render the same narrator portrait side by side unless the pool
  // (3 male + 2 female images) is exhausted.
  private buildNarratorAvatarMap(books: readonly Book[]): Map<string, string> {
    const usedAvatars = new Set<string>();
    const avatarMap = new Map<string, string>();
    const booksNeedingPicks: Book[] = [];

    for (const book of books) {
      if (book.narratorAvatarPath && !usedAvatars.has(book.narratorAvatarPath)) {
        avatarMap.set(book.title, book.narratorAvatarPath);
        usedAvatars.add(book.narratorAvatarPath);
      } else {
        booksNeedingPicks.push(book);
      }
    }

    for (const book of booksNeedingPicks) {
      const avatar = pickNarratorAvatar(book.narrator, book.title, usedAvatars);
      avatarMap.set(book.title, avatar);
      usedAvatars.add(avatar);
    }

    return avatarMap;
  }

  protected getPromotedBook(): Book | undefined {
    if (!this.promotedBookId) {
      return undefined;
    }

    try {
      return this.sampleContentService.getBook(this.promotedBookId);
    } catch {
      // The promoted book has no sample content in this locale's manifest;
      // better to break the ad's promise than the funnel.
      return undefined;
    }
  }

  protected getRelevantBooks(): Book[] {
    if (this.projectName === ProjectNames.FUNNEL) {
      return this.sampleContentService.getBooks(this.getMainFunnelRelevantBookIds());
    }

    const relevantBookIds: BookId[] = [];

    const addBookId = (target: BookId[], bookId: BookId | undefined) => {
      if (bookId && !target.includes(bookId)) {
        target.push(bookId);
      }
    };
    const addBookIds = (target: BookId[], bookIds: readonly BookId[]) => {
      bookIds.forEach(bookId => addBookId(target, bookId));
    };

    this.motivationIds.forEach(motivationId =>
      addBookId(relevantBookIds, motivationBookIdMap[motivationId])
    );
    this.challengeIds.forEach(challengeId =>
      addBookId(relevantBookIds, challengesBookIdMap[challengeId])
    );

    if (this.goalId) {
      addBookIds(relevantBookIds, goalsBookIdsMap[this.goalId] || []);
    }

    addBookIds(relevantBookIds, defaultBookIds);

    return this.sampleContentService.getBooks(relevantBookIds);
  }

  // Every answer that relates to a book becomes a group, and the groups are interleaved so book1
  // and book2 come from different answers - one strong motivation no longer fills both slots.
  // Motivations lead because they are the user's own words for what they want from reading.
  private getMainFunnelRelevantBookIds(): BookId[] {
    const motivationBookIds = interleaveBookIds(
      this.motivationIds.map(motivationId => mainFunnelMotivationBookIdsMap[motivationId] ?? [])
    );
    const struggleBookIds = interleaveBookIds([
      this.getMappedBookIds(procrastinationBookIdsMap, this.procrastinationFrequency),
      this.getMappedBookIds(anxietyBookIdsMap, this.anxietyFeeling),
    ]);
    const personTypeBookIds = this.getMappedBookIds(personTypeBookIdsMap, this.personalityType);
    const goalBookIds = this.goalId ? mainFunnelGoalBookIdsMap[this.goalId] : [];

    const rankedBookIds = interleaveBookIds([
      motivationBookIds,
      struggleBookIds,
      personTypeBookIds,
      goalBookIds,
    ]);

    return Array.from(new Set([...rankedBookIds, ...defaultBookIds]));
  }

  private getMappedBookIds<K extends string>(
    map: Partial<Record<K, readonly BookId[]>>,
    answer: K | undefined
  ): readonly BookId[] {
    return (answer && map[answer]) || [];
  }

  // [celeb1, celeb2, celeb3] for the current user. Lineups are driven by
  // 90-day celeb vote data (yes/(yes+no) on web_quiz_step_finished
  // key=celebN + asset, Aug 2026): Freeman 87% stays the opener for
  // everyone; Oprah collapsed to 48% with women and Robin Williams was
  // the weakest man at 75%, so both were swapped for Brad Pitt and
  // Zendaya - the two voices the app actually ships. Voices cross gender
  // lines (Portman holds 77% with men), so lineups keep mixing genders
  // instead of mirroring the user's: men get Zendaya, women keep Portman.
  protected getCelebLineup(): Celebrity[] {
    if (this.gender === 'male') {
      return [celebrities.morganFreeman, celebrities.bradPitt, celebrities.zendaya];
    }

    if (this.gender === 'female') {
      return [celebrities.morganFreeman, celebrities.nataliePortman, celebrities.zendaya];
    }

    return [celebrities.morganFreeman, celebrities.nataliePortman, celebrities.bradPitt];
  }

  private getNarratorAvatarPath(book: Book): string {
    return this.narratorAvatarMap.get(book.title) ?? pickNarratorAvatar(book.narrator, book.title);
  }

  protected findDiverseBook(candidates: Book[], selectedBooks: Book[]): Book | undefined {
    const selectedNarrators = new Set(selectedBooks.map(book => book.narrator));

    return (
      candidates.find(
        candidate =>
          !selectedBooks.includes(candidate) && !selectedNarrators.has(candidate.narrator)
      ) || this.findBookExcluding(candidates, selectedBooks)
    );
  }

  private findBookExcluding(candidates: Book[], selectedBooks: Book[]): Book | undefined {
    return candidates.find(candidate => !selectedBooks.includes(candidate));
  }

  // The two demo pairs a visitor hears, by their formats, then their goal, then the default
  // order: demo1/demo2 play the first pair, demo3/demo4 the second.
  private getDemoPairs(): [DemoPairKey, DemoPairKey] {
    const byFormats = this.getSelectedFormats()
      .map(format => FORMAT_DEMO_PAIRS[format])
      .filter((pair): pair is DemoPairKey => !!pair);
    const byGoal = (this.goalId && GOAL_DEMO_PAIRS[this.goalId]) || [];
    const [first, second] = Array.from(
      new Set([...byFormats, ...byGoal, ...DEFAULT_DEMO_PAIR_ORDER])
    );

    return [first, second];
  }

  private buildDemoStep(key: QuizStepKey): Quiz | undefined {
    const [firstPair, secondPair] = this.getDemoPairs().map(pair => DEMO_PAIRS[pair]);

    // For demo1..4 use first pair (A/B) then second pair (A/B)
    const sequence: readonly [readonly string[], 0 | 1][] = [
      [firstPair, 0],
      [firstPair, 1],
      [secondPair, 0],
      [secondPair, 1],
    ];

    const orderIndex = ['demo1', 'demo2', 'demo3', 'demo4'].indexOf(key);
    if (orderIndex < 0) return;

    const [pair, idx] = sequence[orderIndex];
    const base = pair[idx];
    const lib = this.sampleContentService.getDemo(base);

    return {
      ...this.steps[key],
      assetId: base,
      title: this.buildDemoTitle(orderIndex, lib.label),
      subtitle: this.buildDemoSubtitle(orderIndex),
      isVoting: true,
      textDemo: {
        audioAssetPath: lib.audioPath,
        fullText: lib.fullText,
        markups: lib.markups,
      },
    } as Quiz;
  }

  private buildDemoTitle(index: number, label: string): string {
    const kind = label === 'E-Mail' ? label : label.toLowerCase();

    switch (index) {
      case 0:
        return `${label}: ${$localize`:@@demoTitle0Suffix:quick listen and rate`}`;
      case 1:
        return $localize`:@@demoTitle1Prefix:Another sound for this ` + kind;
      case 2:
        return (
          $localize`:@@demoTitle2Prefix:New ` +
          kind +
          $localize`:@@demoTitle2Suffix: sample - listen!`
        );
      case 3:
        return $localize`:@@demoTitle3Prefix:Different sound for this ` + kind;
      default:
        return $localize`:@@demoTitleDefault:Demo`;
    }
  }

  private buildDemoSubtitle(index: number): string | undefined {
    if (index === 1) {
      const text =
        $localize`:@@demoSubtitlePopularPrefix:Popular with ` +
        highlightText($localize`:@@listeners15M:1.5M+`) +
        $localize`:@@demoSubtitlePopularSuffix: listeners`;
      return `<i class="fs-12">${text}</i>`;
    }

    if (index === 3) {
      const text = $localize`:@@demoSubtitleFresh:A fresh take — give it a try`;
      return `<i class="fs-12">${text}</i>`;
    }

    return `<i class="fs-12">${$localize`:@@demoSubtitleDefault:Tap play, then rate`}</i>`;
  }
}
